import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import { ConversionPreviewBanner } from '../components/canvas/ConversionPreviewBanner';
import { WorkflowHeader } from '../components/workflow/WorkflowHeader';
import { ExecutionHistoryTable } from '../components/workflow/ExecutionHistoryTable';
import { RunDetailModal } from '../components/workflow/RunDetailModal';
import { TriggerRunForm } from '../components/workflow/TriggerRunForm';
import WorkflowProtectionsPanel from '../components/workflow/WorkflowProtectionsPanel';
import WorkflowEnforcementAuditPanel from '../components/workflow/WorkflowEnforcementAuditPanel';
import { useConversionReport } from '../hooks/useConversionReport';
import { Button, PromoteSlideout } from '../components/v2';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../services/api';

/**
 * WorkflowDetailPage: native workflow detail page at /workflows/:id
 *
 * Layout:
 * 1. WorkflowHeader (top bar with back button, name, source badge)
 * 2. Conversion report banner (if applicable)
 * 3. Canvas DAG renderer (read-only)
 * 4. Execution panel (tabbed: Runs + Trigger New Run)
 * 5. Run detail modal (opens on "View" click)
 *
 * Data fetching:
 * - GET  /api/v1/workflows/{id}?expand=source,binding,runs (this page)
 * - GET  /api/v1/workflows/{id}/runs                       (ExecutionHistoryTable)
 * - GET  /api/v1/workflows/runs/{runId}                    (RunDetailModal)
 * - POST /api/v1/workflows/{id}/execute                    (TriggerRunForm)
 *
 * Single canonical surface — see WorkflowController + WorkflowDetailDto
 * (refactor #36). The prior NativeWorkflowController is retired; its
 * old paths return 410 Gone via LegacyNativeWorkflowController.
 */
export default function WorkflowDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('runs');
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [showRunDetail, setShowRunDetail] = useState(false);
  /* Phase 9 — Promote affordance state. Loaded by PromoteSlideout from
   * the source tenant's Promotion Graph; gated at button level by
   * promotion.initiate AND a known source instance. */
  const [promoteOpen, setPromoteOpen] = useState(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        setLoading(true);
        // Unified workflow detail endpoint with explicit enrichment.
        // The lean shape (no expand) is what the canvas editor uses;
        // we pass `?expand=source,binding,runs` here to opt into the
        // detail-page enrichments (ConversionSource + reportId,
        // workerBindingStatus, latestRun + recentRuns).
        // Routed through the shared apiFetch (auth/refresh/CSRF/cookie). It
        // throws on non-OK with `err.status` attached, so the 404 →
        // "Workflow not found" UX is preserved without string-matching.
        const res = await apiFetch(`/api/v1/workflows/${id}?expand=source,binding,runs`);
        setWorkflow(res.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch workflow:', err);
        setError(err.status === 404 ? 'Workflow not found' : err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [id]);

  // P1-C: Lossy conversion-report pipeline — fetch the report for the source
  // Conductor workflow that generated this native workflow. The hook no-ops
  // when the id is null (either no source link, or non-Conductor workflow).
  // The banner itself degrades gracefully when `report` is null.
  const discoveredWorkflowId = workflow?.source?.discoveredWorkflowId;
  const { report: conversionReport } = useConversionReport(discoveredWorkflowId);

  const handleViewExecution = (runId) => {
    setSelectedRunId(runId);
    setShowRunDetail(true);
  };

  const handleExecutionStarted = (runId) => {
    // Execution started successfully, switch to Runs tab to show the new run
    setActiveTab('runs');
  };

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <WorkflowHeader workflow={workflow} onBack={() => navigate(-1)} />

      {/* Phase 9 — Promote action row. Sits in its own bar below the
       * existing WorkflowHeader so we don't need to thread an actions
       * slot through that component (it's used by other pages). Only
       * rendered when the workflow is instance-scoped and the caller
       * holds promotion.initiate. */}
      {workflow?.instanceId && hasPermission && hasPermission('promotion.initiate') && (
        <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Send size={13} />}
            onClick={() => setPromoteOpen(true)}
            title="Promote this workflow to another instance (Dev → Staging → Prod, etc.)"
          >
            Promote
          </Button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Conversion report banner — only rendered when the backend has
                a report on file for the source Conductor workflow (see
                useConversionReport + NativeWorkflowController reverse lookup).
                Non-Conductor workflows and Conductor workflows without a report
                simply hide the banner rather than showing an empty box. */}
            {workflow?.source?.type === 'CONDUCTOR' && conversionReport && (
              <ConversionPreviewBanner
                report={conversionReport}
                mode="summary"
              />
            )}

            {/* Canvas DAG renderer */}
            {workflow?.definition && (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Workflow Definition</h2>
                <div className="bg-gray-50 rounded border border-gray-200 p-4">
                  <pre className="text-xs overflow-auto max-h-96 text-gray-700">
                    {JSON.stringify(workflow.definition, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Execution panel (tabbed) */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              {/* Tabs */}
              <div className="border-b border-gray-200 flex">
                <button
                  onClick={() => setActiveTab('runs')}
                  className={`px-6 py-3 font-medium text-sm transition ${
                    activeTab === 'runs'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Execution History
                </button>
                <button
                  onClick={() => setActiveTab('trigger')}
                  className={`px-6 py-3 font-medium text-sm transition ${
                    activeTab === 'trigger'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Trigger New Run
                </button>
                <button
                  onClick={() => setActiveTab('protections')}
                  className={`px-6 py-3 font-medium text-sm transition ${
                    activeTab === 'protections'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Data Protections
                </button>
                <button
                  onClick={() => setActiveTab('audit')}
                  className={`px-6 py-3 font-medium text-sm transition ${
                    activeTab === 'audit'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Enforcement Audit
                </button>
              </div>

              {/* Tab content */}
              <div className="p-6">
                {activeTab === 'runs' && (
                  <ExecutionHistoryTable
                    workflowId={id}
                    onViewExecution={handleViewExecution}
                  />
                )}
                {activeTab === 'trigger' && (
                  <TriggerRunForm
                    workflowId={id}
                    canvasDefinition={workflow?.definition}
                    onExecutionStarted={handleExecutionStarted}
                  />
                )}
                {activeTab === 'protections' && (
                  <WorkflowProtectionsPanel
                    workflowId={id}
                    canManage={workflow?.canManage !== false}
                  />
                )}
                {activeTab === 'audit' && (
                  <WorkflowEnforcementAuditPanel
                    workflowId={id}
                    runs={workflow?.recentRuns || []}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Run detail modal */}
      <RunDetailModal
        isOpen={showRunDetail}
        workflowId={id}
        runId={selectedRunId}
        onClose={() => setShowRunDetail(false)}
      />
      {/* Phase 9 — Promote Workflow slideout. Only mount once we have
       * the workflow row in hand so the slideout's internal effects
       * (which fetch the source tenant's Promotion Graph) get stable
       * props on first render. */}
      {workflow && (
        <PromoteSlideout
          isOpen={promoteOpen}
          onClose={() => setPromoteOpen(false)}
          resourceType="WORKFLOW"
          resourceId={workflow.id}
          resourceName={workflow.name}
          sourceInstanceId={workflow.instanceId}
          sourceInstanceName={workflow.instanceName}
          sourceTenantId={workflow.tenantId}
        />
      )}
    </div>
  );
}
